import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Layout, MenuItemLink, DashboardMenuItem, usePermissions } from 'react-admin';
import { List, ListItem, ListItemIcon, ListItemText, Collapse } from '@material-ui/core';
import {
    Gavel, Cancel, Assessment, AccountBalanceWallet, VerifiedUser, AttachMoney, History,
    Security, AssignmentTurnedIn, CardGiftcard, Group, LocalLaundryService,
    MonetizationOn, ContactSupport, Work, Image, Category, Description, BarChart,
    Timeline, Build, CallSplit, Security as ShieldIcon, AccountTree, ExpandLess, ExpandMore,
    Translate, Business, PeopleOutline, Public, Redeem, ToggleOn, Tune,
} from '@material-ui/icons';
import { hasAccess } from '../utils/permissions';

// Sidebar navigation grouped into collapsible sections aligned with the admin
// RBAC roles. Each item is role-gated via hasAccess; a group is hidden entirely
// when the user can access none of its items. The menu is an accordion: only the
// section containing the current route (or the one last clicked) is expanded.
//
// Menu structure is fully owned here (we do not render react-admin's stock
// <Menu>, which would render a flat, ungrouped list and duplicate the links).

export const MENU_GROUPS = [
    {
        id: 'overview',
        label: 'Overview',
        icon: Timeline,
        items: [
            { key: 'dashboard', to: '/', primaryText: 'Dashboard', dashboard: true },
            { key: 'page-analytics', to: '/page-analytics', primaryText: 'Page Analytics', icon: Assessment },
            { key: 'user-journey', to: '/user-journey', primaryText: 'User Journey', icon: CallSplit },
            { key: 'recently-active-users', to: '/recently-active-users', primaryText: 'Recently Active', icon: PeopleOutline },
            { key: 'guest-visitors', to: '/guest-visitors', primaryText: 'Guest Visitors', icon: Public },
            { key: 'reports', to: '/reports', primaryText: 'Reports', icon: BarChart },
        ],
    },
    {
        id: 'operations',
        label: 'Operations',
        icon: AccountTree,
        items: [
            { key: 'users', to: '/users', primaryText: 'Users', icon: Group },
            { key: 'requests', to: '/requests', primaryText: 'Requests', icon: Work },
            { key: 'userservices', to: '/userservices', primaryText: 'Registered Services', icon: LocalLaundryService },
            { key: 'defaultservices', to: '/defaultservices', primaryText: 'Default Services', icon: Category },
            { key: 'feeds', to: '/feeds', primaryText: 'Feeds', icon: Image },
            { key: 'communities', to: '/communities', primaryText: 'Communities', icon: Business },
        ],
    },
    {
        id: 'trust-safety',
        label: 'Trust & Safety',
        icon: ShieldIcon,
        items: [
            { key: 'disputes', to: '/disputes', primaryText: 'Disputes', icon: Gavel },
            { key: 'cancellations', to: '/cancellations', primaryText: 'Cancellations', icon: Cancel },
            { key: 'contact-moderation', to: '/contact-moderation', primaryText: 'Contact Moderation', icon: Security },
            { key: 'tickets', to: '/tickets', primaryText: 'Tickets', icon: ContactSupport },
            // Support investigation tool (gate SUPPORT/FINANCE/OPERATIONS), grouped
            // with the other support tooling rather than under Finance.
            { key: 'referral-rewards', to: '/referral-rewards', primaryText: 'Referral Rewards', icon: Redeem },
            { key: 'verification-queue', to: '/verification-queue', primaryText: 'Verification', icon: VerifiedUser },
            { key: 'credentials-review', to: '/credentials-review', primaryText: 'Credentials', icon: AssignmentTurnedIn },
        ],
    },
    {
        id: 'finance',
        label: 'Finance',
        icon: MonetizationOn,
        items: [
            { key: 'financials', to: '/financials', primaryText: 'Financials', icon: MonetizationOn },
            { key: 'pricing', to: '/pricing', primaryText: 'Pricing', icon: AttachMoney },
            { key: 'debt', to: '/debt', primaryText: 'Provider Debt', icon: AccountBalanceWallet },
            { key: 'test-credits', to: '/test-credits', primaryText: 'Test Credits', icon: CardGiftcard },
        ],
    },
    {
        id: 'system',
        label: 'System',
        icon: Build,
        items: [
            { key: 'logs', to: '/logs', primaryText: 'Logs', icon: Description },
            { key: 'audit-logs', to: '/audit-logs', primaryText: 'Admin Audit Log', icon: History },
            { key: 'translations', to: '/translations', primaryText: 'Translations', icon: Translate },
            { key: 'meta-flags', to: '/meta-flags', primaryText: 'Meta Feature Flags', icon: ToggleOn },
            { key: 'feature-flags', to: '/feature-flags', primaryText: 'Feature Flags', icon: Tune },
        ],
    },
];

// Resolve which group owns the current pathname, so it can auto-expand.
const groupIdForPath = pathname => {
    for (const group of MENU_GROUPS) {
        for (const item of group.items) {
            if (item.to === '/') {
                if (pathname === '/') return group.id;
            } else if (pathname === item.to || pathname.startsWith(item.to + '/')) {
                return group.id;
            }
        }
    }
    return null;
};

const BrandedMenu = props => {
    const open = useSelector(state => state.admin.ui.sidebarOpen);
    const { permissions } = usePermissions();
    const adminRoles = (permissions && permissions.adminRoles) || [];
    const location = useLocation();

    // Only keep groups the user can see something in.
    const visibleGroups = useMemo(
        () =>
            MENU_GROUPS.map(group => ({
                ...group,
                items: group.items.filter(item => hasAccess(item.key, adminRoles)),
            })).filter(group => group.items.length > 0),
        [adminRoles]
    );

    const activeGroupId = groupIdForPath(location.pathname);
    // Accordion: at most one group is expanded at a time, so the nav list never
    // grows past its container as the user moves between sections.
    const [openGroupId, setOpenGroupId] = useState(activeGroupId);

    // When navigating into another group, open it and collapse the rest.
    useEffect(() => {
        setOpenGroupId(activeGroupId);
    }, [activeGroupId]);

    const toggleGroup = id =>
        setOpenGroupId(prev => (prev === id ? null : id));

    const renderItem = item => {
        const ItemIcon = item.icon;
        if (item.dashboard) {
            return (
                <DashboardMenuItem
                    key={item.key}
                    sidebarIsOpen={open}
                    leftIcon={ItemIcon ? <ItemIcon /> : undefined}
                    onClick={props.onMenuClick}
                />
            );
        }
        return (
            <MenuItemLink
                key={item.key}
                to={item.to}
                primaryText={item.primaryText}
                leftIcon={ItemIcon ? <ItemIcon /> : undefined}
                sidebarIsOpen={open}
                onClick={props.onMenuClick}
            />
        );
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 73px)', overflow: 'auto' }}>
            <div className={open ? 'sk-brand sk-brand--open' : 'sk-brand'} aria-label="Skilz Admin">
                <span className="sk-brand-mark" aria-hidden="true">s</span>
                {open && <span className="sk-brand-name">skilz<span>.</span></span>}
            </div>
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', scrollbarGutter: 'stable' }}>
                {/* Collapsed rail: flat icon-only list (no group toggles fit the rail) */}
                {!open
                    ? visibleGroups.map(group => group.items.map(renderItem))
                    : visibleGroups.map(group => {
                          const GroupIcon = group.icon;
                          const isOpen = openGroupId === group.id;
                          return (
                              <List key={group.id} disablePadding component="nav">
                                  <ListItem
                                      button
                                      onClick={() => toggleGroup(group.id)}
                                      className="sk-menu-group"
                                      aria-expanded={isOpen}
                                  >
                                      <ListItemIcon style={{ minWidth: 34 }}>
                                          <GroupIcon fontSize="small" />
                                      </ListItemIcon>
                                      <ListItemText
                                          primary={group.label}
                                          primaryTypographyProps={{ className: 'sk-menu-group-label' }}
                                      />
                                      {isOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                                  </ListItem>
                                  <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                      <div className="sk-menu-group-items">
                                          {group.items.map(renderItem)}
                                      </div>
                                  </Collapse>
                              </List>
                          );
                      })}
            </div>
        </div>
    );
};

const AdminLayout = props => <Layout {...props} menu={BrandedMenu} />;

export default AdminLayout;
